import { deepCopy } from 'commons/utils';
import store from 'commons/store/Store';
import {
  Composition,
  SmartComposition,
  AdvancedComposition,
  TranslationValue,
  PublishingCategoryConfig,
  Sellable,
  RestrictedAppearanceGroup,
  getCategories,
  hasApparelCategory,
  getProductTypeImage as _getProductTypeImage,
  getProductImage as _getProductImage,
  AdvancedTemplateProduct,
  CompositionMode,
} from '@spreadgroup/common-partner-lib/composition';
import { HotspotKey } from '@spreadgroup/common-partner-lib/states';
import { ExtendedApparelTargetGroupLabel } from '@spreadgroup/common-partner-lib/publishing';
import { getCombinedDesignsImageUrl } from 'commons/image/imageUrlBuilder.ts';
import { readonly } from 'vue';

export const printAreas = readonly<
  Record<
    string,
    {
      name: string;
      hotspots: HotspotKey[];
    }
  >
>({
  FRONT: {
    name: 'DESIGNS.PRINTAREA.LOCATION.FRONT',
    hotspots: [
      'FULL_FRONT',
      'MEDIUM_FRONT',
      'LEFT_CHEST',
      'RIGHT_CHEST',
      'CENTER_CHEST',
    ],
  },
  BACK: {
    name: 'DESIGNS.PRINTAREA.LOCATION.BACK',
    hotspots: ['FULL_BACK', 'MEDIUM_BACK', 'BACK_COLLAR'],
  },
  LEFT: {
    name: 'DESIGNS.PRINTAREA.LOCATION.LEFT',
    hotspots: ['LEFT'],
  },
  RIGHT: {
    name: 'DESIGNS.PRINTAREA.LOCATION.RIGHT',
    hotspots: ['RIGHT'],
  },
});

export const compositionModeSettings: Record<
  CompositionMode,
  { name: string; key: string; bgClass: string; icon?: string }
> = {
  [CompositionMode.SMART]: {
    name: 'PUBLISHING.DETAIL.PRODUCTS.MODES.SMART',
    key: 'SMART',
    bgClass: 'mode-smart-bg',
    icon: 'smart-mode',
  },
  [CompositionMode.ADVANCED]: {
    name: 'PUBLISHING.DETAIL.PRODUCTS.MODES.ADVANCED',
    key: 'ADVANCED',
    bgClass: 'mode-advanced-bg',
    icon: 'advanced-mode',
  },
  [CompositionMode.UNSUPPORTED]: {
    name: 'Unsupported',
    key: 'UNSUPPORTED',
    bgClass: '',
  },
};

export default {
  getMetaDataPart({
    composition,
    part,
    language,
  }: {
    composition: Composition;
    part: 'name' | 'description' | 'tags';
    language?: string;
  }) {
    const metaData = composition.metaData[part] || [];

    return metaData.find(
      (value: TranslationValue) =>
        value.language ===
        (language || composition.metaData.originLanguageCode),
    );
  },
  getDefaultMetaData(composition: Composition) {
    return {
      name: this.getMetaDataPart({ composition, part: 'name' }),
      description: this.getMetaDataPart({ composition, part: 'description' }),
      tags: this.getMetaDataPart({ composition, part: 'tags' }),
    };
  },
  addEmptyDataStructureForDefaultMetaData(composition: Composition) {
    const comp = deepCopy(composition);

    if (
      !comp.metaData.name?.find(
        (v: TranslationValue) =>
          v.language === composition.metaData.originLanguageCode,
      )
    ) {
      comp.metaData.name = [
        ...(comp.metaData.name || []),
        {
          language: comp.metaData.originLanguageCode,
          value: '',
        },
      ];
    }
    if (
      !comp.metaData.description?.find(
        (v: TranslationValue) =>
          v.language === composition.metaData.originLanguageCode,
      )
    ) {
      comp.metaData.description = [
        ...(comp.metaData.description || []),
        {
          language: comp.metaData.originLanguageCode,
          value: '',
        },
      ];
    }
    if (
      !comp.metaData.tags?.find(
        (v: TranslationValue) =>
          v.language === composition.metaData.originLanguageCode,
      )
    ) {
      comp.metaData.tags = [
        ...(comp.metaData.tags || []),
        {
          language: comp.metaData.originLanguageCode,
          values: [],
        },
      ];
    }

    return comp;
  },
  resetMetaData(composition: Composition) {
    const comp = deepCopy(composition);

    delete comp.metaData.name;
    delete comp.metaData.description;
    delete comp.metaData.tags;

    return this.addEmptyDataStructureForDefaultMetaData(comp);
  },
  getDesignImage(
    design: number,
    { height = 300, width = 300 }: { height?: number; width?: number } = {},
  ) {
    return `${store.state.publishingCore.config?.image?.templates?.design?.replace(
      '$DESIGN',
      design,
    )},width=${width},height=${height}`;
  },
  getMainDesignImage(
    composition: Composition,
    { height = 300, width = 300 }: { height?: number; width?: number } = {},
  ) {
    if (composition.designs?.length > 1) {
      return getCombinedDesignsImageUrl(
        composition.designs.map((d) => d.id),
        { height, width },
      );
    }

    return this.getDesignImage(composition.mainDesign?.id, { height, width });
  },
  getProductImage(vpString: string, viewId: number) {
    return _getProductImage(
      store.state.publishingCore.config?.image?.templates?.product,
      vpString,
      viewId,
    );
  },
  getProductTypeImage(
    productTypeId: number,
    viewId: number,
    appearanceId: number,
  ) {
    return _getProductTypeImage(
      store.state.publishingCore.config?.image?.templates?.productType,
      productTypeId,
      viewId,
      appearanceId,
    );
  },
  getDefaultActivatedSellableImages(composition: Composition): string[][] {
    return composition.sellables.map((sellable: Sellable) => [
      this.getProductImage(sellable.vpString, sellable.defaultViewId),
    ]);
  },
  getAllActivatedSellableImages(composition: Composition): string[][] {
    return composition.sellables.map((sellable: Sellable) => {
      if (!sellable.printViewIds) return [];

      const sortedViewIds = [...sellable.printViewIds].sort((a, b) => {
        if (a === sellable.defaultViewId) return -1;
        if (b === sellable.defaultViewId) return 1;
        return 0;
      });

      return sortedViewIds.map((viewId: number) =>
        this.getProductImage(sellable.vpString, viewId),
      );
    });
  },
  getDefaultHotspot(composition: Composition) {
    return composition.defaultValues.defaultHotspot;
  },
  getHotspotConfigs(composition: Composition) {
    return composition.templateProducts?.[0]?.templateProduct.hotspotConfigs;
  },
  getDefaultAppearanceGroup(composition: Composition) {
    const appearanceGroupTranslations =
      store.state.publishingCore.translations.appearanceGroups;

    const defaultAppearanceGroup =
      composition.defaultValues.defaultAppearanceGroup;
    return !defaultAppearanceGroup
      ? null
      : {
          ...defaultAppearanceGroup,
          name: appearanceGroupTranslations
            ? appearanceGroupTranslations[defaultAppearanceGroup.id]?.name
            : null,
        };
  },
  getDisabledAppearanceGroupIds(composition: SmartComposition) {
    return (
      composition.defaultValues?.restrictedAppearanceGroups?.map(
        (appearanceGroup: RestrictedAppearanceGroup) => appearanceGroup.id,
      ) || []
    );
  },
  getCategories(composition: SmartComposition) {
    const mainCategoriesList = Object.values(
      store.state.publishingCore.mainCategories,
    ) as PublishingCategoryConfig[];
    return getCategories(composition, mainCategoriesList);
  },
  hasApparelCategory(composition: SmartComposition) {
    return hasApparelCategory(this.getCategories(composition));
  },
  transformCategoryListToAssortmentRule({
    categories,
    labelsForCategory,
  }: {
    categories: PublishingCategoryConfig[];
    labelsForCategory: Record<string, ExtendedApparelTargetGroupLabel[]>;
  }) {
    const mainCategoryCount = Object.values(
      store.state.publishingCore.mainCategories,
    ).length;
    const labelCount = Object.values(
      store.state.publishingCore.apparelTargetGroupLabels,
    ).length;

    const assortmentRule = {
      categories: categories.map((category) => ({
        categoryId: category.id,
        labelTerm:
          labelsForCategory &&
          labelsForCategory[category.id] &&
          labelsForCategory[category.id].length < labelCount // in case all labels are attached no labelTerm is needed
            ? {
                labels: labelsForCategory[category.id].map((label) => ({
                  labelId: label.id,
                  labelGroup: 'MP_CLOTHING_DEPARTMENTS', // todo: refactor hardcoded label group
                })),
                operator: 'OR',
              }
            : undefined,
      })),
    };

    // in case all categories are included and no category is limited by labels we dont need the assortment rule
    if (
      assortmentRule.categories.length === mainCategoryCount &&
      !assortmentRule.categories.some((category) => !!category.labelTerm)
    ) {
      return {};
    } else {
      return assortmentRule;
    }
  },
  getTemplateProductForProductType(
    composition: AdvancedComposition,
    productTypeId: number,
  ) {
    return composition.templateProducts.find(
      (tp) =>
        'productTypeIds' in tp &&
        tp.productTypeIds &&
        tp.productTypeIds.includes(productTypeId),
    ) as AdvancedTemplateProduct | undefined;
  },
  getAppearanceRuleForProductType(
    composition: AdvancedComposition,
    productTypeId: number,
  ) {
    return composition.assortmentRule?.appearanceRules?.find(
      (ar) => ar.productTypeId === productTypeId,
    );
  },
  hasMultipleDesigns: (composition?: Composition): boolean =>
    !!(composition?.designs && composition?.designs?.length > 1),
};
